class DetailshopPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-search-input]');
    this.form = this.querySelector('[data-search-form]');
    this.shell = this.querySelector('[data-search-shell]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.openButton = this.querySelector('[data-search-open]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.defaultMarkup = this.results?.innerHTML || '';
    this.abortController = null;
    this.debounceTimer = null;

    this.openButton?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.clearButton?.addEventListener('click', () => this.clear());
    this.input?.addEventListener('focus', () => this.activate());
    this.input?.addEventListener('input', () => this.onInput());
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.deactivate();
    });
  }

  open() {
    this.classList.add('is-open', 'is-active');
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('search-open');
    window.requestAnimationFrame(() => this.input?.focus());
  }

  close() {
    this.classList.remove('is-open', 'is-active');
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.input?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('search-open');
    this.openButton?.focus();
  }

  activate() {
    this.classList.add('is-active');
    this.input?.setAttribute('aria-expanded', 'true');
  }

  deactivate() {
    if (this.classList.contains('is-open')) return;
    this.classList.remove('is-active');
    this.input?.setAttribute('aria-expanded', 'false');
  }

  clear() {
    this.input.value = '';
    this.clearButton.hidden = true;
    this.results.innerHTML = this.defaultMarkup;
    this.setLoading(false);
    this.input.focus();
  }

  onInput() {
    const query = this.input.value.trim();
    this.clearButton.hidden = query.length === 0;
    window.clearTimeout(this.debounceTimer);
    this.abortController?.abort();

    if (query.length < 2) {
      this.results.innerHTML = this.defaultMarkup;
      this.setLoading(false);
      return;
    }

    this.setLoading(true);
    this.debounceTimer = window.setTimeout(() => this.fetchResults(query), 180);
  }

  async fetchResults(query) {
    this.abortController = new AbortController();
    const params = new URLSearchParams({
      q: query,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,query',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
    });

    try {
      const response = await fetch(`${this.dataset.predictiveUrl}?${params}`, {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const markup = await response.text();
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const section = documentFragment.querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Predictive search section missing');
      this.results.innerHTML = section.innerHTML;
      const resultCount = this.results.querySelector('[data-result-count]')?.dataset.resultCount || '0';
      this.status.textContent = `${resultCount} resultaten`;
    } catch (error) {
      if (error.name !== 'AbortError') this.form.submit();
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.input?.setAttribute('aria-busy', String(isLoading));
    if (isLoading) this.status.textContent = this.dataset.loadingText;
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.classList.contains('is-open')) this.close();
      else {
        this.deactivate();
        this.input?.blur();
      }
      return;
    }

    if (event.key !== 'ArrowDown' || document.activeElement !== this.input) return;
    const firstResult = this.results.querySelector('a');
    if (firstResult) {
      event.preventDefault();
      firstResult.focus();
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', DetailshopPredictiveSearch);
}
